// Iter68 — Customers admin converted to profile-row card layout.
// Avatar (or LATM default logo), name, email, orders, LTV, joined.

import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Users, Mail, DollarSign } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import AdminFeatureShell from "@/components/admin/AdminFeatureShell";
import SafeImage, { LATM_FALLBACK_LOGO } from "@/components/SafeImage";

const LATM_LOGO = LATM_FALLBACK_LOGO;

function money(cents) {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

function Avatar({ src, name }) {
  return (
    <div className="relative w-12 h-12 rounded-full overflow-hidden bg-black/40 border border-white/10 shrink-0">
      <SafeImage
        src={src || LATM_LOGO}
        alt={name}
        className="absolute inset-0 w-full h-full object-cover"
      />
    </div>
  );
}

export default function AdminCustomers() {
  const [data, setData] = useState({ customers: [], count: 0, total: 0 });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/storefront/customers", {
        params: { q: q || undefined, limit: 200 },
      });
      setData(data);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <AdminFeatureShell
      breadcrumb="LiftedAtTheMoment / CRM"
      title="Customers"
      description="All ecosystem customers (role = customer). LTV is computed from native Lifted Core paid orders."
      status={`${data.total} total customers`}
      testId="admin-customers"
    >
      <div className="glass-panel p-3 flex items-center gap-2">
        <Search className="h-4 w-4 text-lifted-dim" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by email or name…"
          className="flex-1 bg-transparent text-sm focus:outline-none"
          data-testid="customers-search"
        />
      </div>

      {err && <div className="glass-panel border border-rose-500/30 p-3 text-rose-200 text-sm">{err}</div>}

      {/* Profile-row cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="customers-grid">
        {loading && (
          <div className="col-span-full text-center text-lifted-dim py-8">Loading…</div>
        )}
        {!loading && data.customers.length === 0 && (
          <div className="col-span-full text-center text-lifted-dim py-8">No customers match.</div>
        )}
        {data.customers.map((c) => (
          <Link
            key={c.id}
            to={`/admin/users?focus=${encodeURIComponent(c.email)}`}
            data-testid={`customer-row-${c.id}`}
            className="glass-panel p-3 flex items-center gap-3 hover:border-lifted-green/30 border border-transparent transition-colors"
          >
            <Avatar src={c.avatar_url} name={c.name} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-lifted-text truncate">{c.name || "—"}</div>
              <div className="text-xs text-lifted-dim truncate inline-flex items-center gap-1">
                <Mail className="h-3 w-3" />{c.email}
              </div>
              <div className="text-[10px] text-lifted-mute mt-1">
                {(c.city || c.state) && (
                  <span>{[c.city, c.state].filter(Boolean).join(", ")} · </span>
                )}
                Joined {c.created_at?.slice(0, 10) || "—"}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-anton text-lg text-lifted-green leading-none">{money(c.ltv_cents)}</div>
              <div className="text-[9px] text-lifted-mute uppercase tracking-wider">{c.orders_count} orders</div>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="customers-summary">
        <div className="glass-panel p-4">
          <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase inline-flex items-center gap-1.5">
            <Users className="h-3 w-3 text-lifted-green" /> Total
          </div>
          <div className="font-anton text-2xl text-white mt-1">{data.total}</div>
        </div>
        <div className="glass-panel p-4">
          <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase inline-flex items-center gap-1.5">
            <DollarSign className="h-3 w-3 text-lifted-green" /> With orders
          </div>
          <div className="font-anton text-2xl text-white mt-1">
            {data.customers.filter((c) => c.orders_count > 0).length}
          </div>
        </div>
        <div className="glass-panel p-4">
          <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase inline-flex items-center gap-1.5">
            <DollarSign className="h-3 w-3 text-lifted-green" /> Total LTV (shown)
          </div>
          <div className="font-anton text-2xl text-white mt-1">
            {money(data.customers.reduce((s, c) => s + (c.ltv_cents || 0), 0))}
          </div>
        </div>
      </div>
    </AdminFeatureShell>
  );
}
